/*!
 * Adapted from "The Furrow - ASCIIxGPT - Concept 5" by The Furrow
 * (https://thefurrow.tv/project/asciixgpt). Changes: ported to a WebGL2 shader,
 * with a different pattern set, glyphs, colors, settings, and a carve around content.
 * This file is licensed under CC BY-NC-SA 4.0:
 * https://creativecommons.org/licenses/by-nc-sa/4.0/
 */

// The page's palettes. Each wave repaints the page with a different one: the field's cells and
// the accent color of the links, the bio, and the switches change as the wave's front passes
// them, and the page keeps the new palette after the wave. The background, the text, and the
// portrait keep their colors.
//
// This is just data, with no engine import, because the 404 page takes its accent from here.
// palette-waves.ts does the actual painting.

import type { Theme } from "../page/theme.js";

/** One palette's colors in one theme. */
interface ThemePalette {
  /** The field's glyph colors: charset index `i` draws in `field[i % n]`, fading toward
      `field[(i + 1) % n]`. */
  readonly field: readonly string[];
  /** The accent (`--accent` in _theme.scss): text on the background, and the fill behind
      background-colored text on hover. It's the second field color's OKLCH hue and chroma,
      with the lightness moved just enough for AA contrast with the background: darker in
      light, lighter in dark. That keeps the light one well apart from the black text, which
      the darkest field color wouldn't. */
  readonly accent: string;
}

/** A palette in its light form, its dark form, or both. A palette with only one form shows
    up only in that theme. */
export type Palette =
  | { readonly name: string; readonly light: ThemePalette; readonly dark?: ThemePalette }
  | { readonly name: string; readonly light?: ThemePalette; readonly dark: ThemePalette };

// The page's own palette (_theme.scss), in both forms. Light comes from The Furrow: cream,
// orange, brick, and maroon. Dark comes from the dark portrait: a faint plum just above the
// charcoal background, the face's dusty pink, the backdrop's mauve and navy, then the face's
// lavender highlight.
const PAGE = {
  name: "page",
  light: { field: ["#ebe4da", "#e58763", "#a35a56", "#450209"], accent: "#923c17" },
  dark: {
    field: ["#3a2e43", "#98747d", "#66576f", "#3d507c", "#b7a2b6"],
    accent: "#a27e87",
  },
} as const satisfies Palette;

// The first alternates keep the page palette's lightness and chroma steps, tier for tier
// (OKLCH), with their own hues, so each one sits on the background the same way.
export const PALETTES: readonly Palette[] = [
  PAGE,
  // Sea: teal and slate blue.
  {
    name: "sea",
    light: {
      field: ["#dae9e8", "#0fb9c1", "#2d7ba3", "#002047"],
      accent: "#026166",
    },
    dark: {
      field: ["#193940", "#5f8983", "#41646c", "#295579", "#90b2af"],
      accent: "#69938d",
    },
  },
  // Moss: olive and sage.
  {
    name: "moss",
    light: {
      field: ["#e6e6db", "#a1ac47", "#528048", "#012a0f"],
      accent: "#545b00",
    },
    dark: {
      field: ["#2d3822", "#798566", "#5f6044", "#2a5e3e", "#afab90"],
      accent: "#838f70",
    },
  },
  // Iris: violet and indigo.
  {
    name: "iris",
    light: {
      field: ["#e6e3ee", "#b68ee2", "#8b5f95", "#1f154d"],
      accent: "#6c4592",
    },
    dark: {
      field: ["#313147", "#797d9b", "#545d76", "#554877", "#a3a9c2"],
      accent: "#8387a5",
    },
  },
  // Gold: ochre and umber.
  {
    name: "gold",
    light: {
      field: ["#e9e5da", "#d1973c", "#9d6237", "#3c1300"],
      accent: "#774f02",
    },
    dark: {
      field: ["#442e1f", "#927b61", "#72564a", "#764038", "#b6a890"],
      accent: "#9c856b",
    },
  },
  // Ink: warm grays.
  {
    name: "ink",
    light: {
      field: ["#e6e5e4", "#ada198", "#79706a", "#281f1c"],
      accent: "#60554d",
    },
    dark: {
      field: ["#363330", "#827f7b", "#605d5a", "#56504b", "#aca9a6"],
      accent: "#8c8985",
    },
  },
  // Normal: the page palette's steps, with new hues.
  {
    name: "rose",
    light: { field: ["#eee1e5", "#e48099", "#9a5a7f", "#390837"], accent: "#923752" },
    dark: { field: ["#412c3c", "#98747b", "#6f5463", "#5e4571", "#c0a1a2"], accent: "#a37e85" },
  },
  {
    name: "coral",
    light: { field: ["#efe2df", "#e8827f", "#a15e43", "#361800"], accent: "#953839" },
    dark: { field: ["#442e20", "#9a7573", "#73564b", "#694b19", "#c0a1a4"], accent: "#a57f7d" },
  },
  {
    name: "olive",
    light: { field: ["#e7e6db", "#bca238", "#707930", "#162700"], accent: "#675600" },
    dark: { field: ["#2f3720", "#887f5f", "#5d6145", "#2f5d3a", "#b5a990"], accent: "#918868" },
  },
  {
    name: "fern",
    light: { field: ["#dfe8e0", "#77b669", "#35835e", "#002920"], accent: "#266316" },
    dark: { field: ["#1d3a30", "#71876c", "#496555", "#005e5e", "#a2af97"], accent: "#778e72" },
  },
  {
    name: "jade",
    light: { field: ["#dce9e4", "#44bc90", "#01847a", "#002729"], accent: "#006346" },
    dark: { field: ["#173a3a", "#658978", "#426661", "#075b6f", "#98b1a0"], accent: "#6b907e" },
  },
  {
    name: "lagoon",
    light: { field: ["#dbe9e7", "#0dbcab", "#00828a", "#002630"], accent: "#006259" },
    dark: { field: ["#183940", "#5f8982", "#406568", "#215777", "#93b2a8"], accent: "#659089" },
  },
  {
    name: "azure",
    light: { field: ["#dce7ee", "#37b1e3", "#4376a9", "#0a1c4f"], accent: "#005e7e" },
    dark: { field: ["#283448", "#628596", "#4c6075", "#4d4b7a", "#91b0ba"], accent: "#6a8d9e" },
  },
  {
    name: "cobalt",
    light: { field: ["#e0e5f0", "#6ba6f1", "#626dab", "#23144b"], accent: "#1d579c" },
    dark: { field: ["#313147", "#6d819b", "#565c76", "#5e4571", "#97adc0"], accent: "#7589a3" },
  },
  {
    name: "indigo",
    light: { field: ["#e1e5f0", "#949af0", "#5072ab", "#00233d"], accent: "#4d4e9d" },
    dark: { field: ["#243547", "#797d9b", "#505e76", "#105a71", "#a9a6c0"], accent: "#8286a4" },
  },
  {
    name: "orchid",
    light: { field: ["#ece2e9", "#cf85c9", "#9c597a", "#43021b"], accent: "#813d7d" },
    dark: { field: ["#452b33", "#90768d", "#705461", "#763f3c", "#b5a3b9"], accent: "#9a8097" },
  },
  // Vibrant: the same lightness steps with more chroma. Neon only shows up in dark, signal
  // only in light.
  {
    name: "scarlet",
    light: { field: ["#fedcd4", "#ff635f", "#b84b00", "#472400"], accent: "#b0071d" },
    dark: { field: ["#562b05", "#e0615c", "#a84811", "#745100", "#f7a3a9"], accent: "#e0615c" },
  },
  {
    name: "tangerine",
    light: { field: ["#fcddce", "#ed7b00", "#c43b15", "#5c0012"], accent: "#894400" },
    dark: { field: ["#5a2522", "#d76f04", "#aa442b", "#992154", "#ebb077"], accent: "#d76f04" },
  },
  {
    name: "citrus",
    light: { field: ["#e4e8ca", "#ada200", "#5f7e00", "#0b3800"], accent: "#5f5800" },
    dark: { field: ["#254112", "#9c9300", "#587500", "#006943", "#d2bd70"], accent: "#9c9300" },
  },
  {
    name: "emerald",
    light: { field: ["#cfeddb", "#00be58", "#008568", "#003631"], accent: "#00652b" },
    dark: { field: ["#004239", "#2ea957", "#007c60", "#00646d", "#9bce90"], accent: "#2ea957" },
  },
  {
    name: "aqua",
    light: { field: ["#c7edf0", "#00b5b5", "#007f95", "#003346"], accent: "#006161" },
    dark: { field: ["#003e51", "#00a4a4", "#00768a", "#005a9d", "#69d3c6"], accent: "#00a4a4" },
  },
  {
    name: "electric",
    light: { field: ["#d7e5ff", "#53a0ff", "#5761d8", "#311969"], accent: "#0056a9" },
    dark: { field: ["#34315f", "#4390ee", "#535eb8", "#733699", "#86c5fa"], accent: "#4390ee" },
  },
  {
    name: "ultraviolet",
    light: { field: ["#e7dffd", "#9f88ff", "#914bbe", "#4b0750"], accent: "#5f42b2" },
    dark: { field: ["#4a2951", "#907ae9", "#814ea4", "#91256c", "#b1b7fd"], accent: "#907ae9" },
  },
  {
    name: "magenta",
    light: { field: ["#f4dcf3", "#e665cf", "#9848b8", "#3a1463"], accent: "#9c178a" },
    dark: { field: ["#422c59", "#c865b5", "#874c9f", "#4b46ae", "#eda4cc"], accent: "#c865b5" },
  },
  {
    name: "neon",
    dark: { field: ["#53295d", "#e45cbe", "#008090", "#7541b8", "#4ddcdc"], accent: "#e45cbe" },
  },
  {
    name: "signal",
    light: { field: ["#f6e1c9", "#fc4926", "#c21725", "#0a2272"], accent: "#a92000" },
  },
  // Varied: several hues each, up to 8 colors, with the page palette's spread of light and
  // dark. Aurora only shows up in dark, candy and bauhaus only in light.
  {
    name: "sunset",
    light: {
      field: ["#efe3cb", "#ef7d88", "#b05f1a", "#361e57", "#eaba83", "#8a3a64"],
      accent: "#9a3041",
    },
    dark: {
      field: ["#3d3052", "#bd735a", "#894d6a", "#3d4e89", "#d1ad84", "#a75b5c"],
      accent: "#bf755c",
    },
  },
  {
    name: "tropic",
    light: {
      field: ["#eae6cb", "#3fbf86", "#b8544f", "#003538", "#b1d092", "#8a3a64"],
      accent: "#006440",
    },
    dark: {
      field: ["#0d4233", "#be706a", "#46703d", "#005e6a", "#beb682", "#9e5b82"],
      accent: "#c0726c",
    },
  },
  {
    name: "earth",
    light: {
      field: ["#ede3d3", "#d89171", "#707f3f", "#0d2f4f", "#d4c396", "#83473e"],
      accent: "#854527",
    },
    dark: {
      field: ["#483321", "#ae7b69", "#65673b", "#225972", "#c5b191", "#996463"],
      accent: "#af7c6a",
    },
  },
  {
    name: "harbor",
    light: {
      field: ["#eee4cb", "#59aaf8", "#b8544f", "#003249", "#78d6db", "#923945"],
      accent: "#00599a",
    },
    dark: {
      field: ["#163c54", "#bc6f81", "#007176", "#305289", "#c5b381", "#a75a61"],
      accent: "#be7183",
    },
  },
  {
    name: "autumn",
    light: {
      field: ["#efe3cb", "#e38d3d", "#b75547", "#540f22", "#c7ca85", "#904016"],
      accent: "#844800",
    },
    dark: {
      field: ["#532b25", "#b57a47", "#8e4c57", "#7a395a", "#beb682", "#a65f46"],
      accent: "#b67b48",
    },
  },
  {
    name: "meadow",
    light: {
      field: ["#e5e7cd", "#72ba63", "#8463b8", "#003822", "#dbc27f", "#7f3e7a"],
      accent: "#1b6404",
    },
    dark: {
      field: ["#1f4127", "#819249", "#6b578f", "#00624b", "#c5b381", "#955e90"],
      accent: "#819249",
    },
  },
  {
    name: "spectrum",
    light: {
      field: [
        "#eae6cb",
        "#a494f6",
        "#b75357",
        "#003538",
        "#f0b788",
        "#175d9b",
        "#d5ecd9",
        "#de80c0",
      ],
      accent: "#5b48a2",
    },
    dark: {
      field: [
        "#3f380e",
        "#887ec2",
        "#904d4e",
        "#005f63",
        "#d6aa87",
        "#4378ad",
        "#1f4127",
        "#b2719c",
      ],
      accent: "#897fc3",
    },
  },
  {
    name: "aurora",
    dark: {
      field: ["#004333", "#44a04b", "#007176", "#543d9e", "#9ec27a", "#007cb7"],
      accent: "#44a04b",
    },
  },
  {
    name: "candy",
    light: {
      field: ["#f9e0eb", "#ea91bc", "#51b48d", "#7d5fad", "#e5d183", "#3a9ec8"],
      accent: "#8a3b64",
    },
  },
  {
    name: "bauhaus",
    light: {
      field: ["#eee8d2", "#de3d34", "#2c5dbd", "#1e212b", "#efcc36", "#c5372f"],
      accent: "#b2000a",
    },
  },
];

/** A palette's form in `theme`. A palette without that form falls back to the page's own. */
export function themePalette(index: number, theme: Theme): ThemePalette {
  return PALETTES[index]?.[theme] ?? PAGE[theme];
}
